/**
 * 신호 문구 조립 — 신호 코드·날짜·시각으로 상태명·한 줄 문구를 만든다(간트 점 · 상세창 카드 등).
 * 근거: 940 시트4 ③ 상황별 표현표(행 29~60) 상태명·한 줄 문구 틀·팝오버 사유·버튼,
 *       ① 공통 규칙(날짜 M/D · 시각 HH:MM · 연도 없음 · 항목 구분 ' · ' · 회신 값 작은따옴표).
 * 시각 고정 표기('07:00' 등)는 쓰지 않는다 — 시각은 값(reasonDtm·nextJudgeDtm)으로 조립한다.
 * 신호 팝오버 · 할일 화면은 서버가 완성한 문구(SIG-01 · TODO-01)를 쓴다 — 이 표는 간트 점 · 상세창 등 남은 신호 표시용이다.
 * 할일 제목 · 사유 · 버튼은 서버 할일 정의(TM_SITE_TODO)가 만든다(화면 상수 없음).
 */
import type { SignalItem, SignalReasonCode, SignalSlot } from './types';

/** 문구 조립에 쓰는 값. 모두 선택 — 없는 값이 들어가는 구절은 생략한다(빈 문구나 'undefined' 를 만들지 않음). */
export interface SignalTextContext {
  /** SignalItem.reasonDate / reasonDtm / nextJudgeDtm 그대로 */
  reasonDate?: string | null;
  reasonDtm?: string | null;
  nextJudgeDtm?: string | null;
  /** 기준일(오늘). 문자열 'YYYY-MM-DD' 또는 Date(로컬 달력일). 생략하면 지금 */
  today?: string | Date;
  /** 공종 착공일 · 종료일 'YYYY-MM-DD' (신호 응답에는 없고 화면이 공종 데이터로 넘긴다) */
  startDate?: string | null;
  endDate?: string | null;
  /** 마지막 전달 건 순위(1-based). 차순위 회신 대기·미확정 문구에 쓴다 */
  rank?: number | null;
  /** 반장·거래처 이름 (있을 때만 문구에 포함) */
  partnerName?: string | null;
  vendorName?: string | null;
  /** 입고 희망일/입고일 'YYYY-MM-DD' */
  receiptDate?: string | null;
  /** 작업지시 수 · 보고한 날 수 · 작업 일수 */
  orderCount?: number | null;
  reportDays?: number | null;
  totalDays?: number | null;
  /** 이슈 보고 유형·내용 */
  issueType?: string | null;
  issueContent?: string | null;
  /** 하차사진·상차사진 수 · 기사 */
  photoCount?: number | null;
  driverName?: string | null;
  driverPhone?: string | null;
}

/** SignalItem 에서 문구 값(reasonDate·reasonDtm·nextJudgeDtm)을 뽑아 나머지 값과 합친다. */
export function signalTextContext(
  item: Pick<SignalItem, 'reasonDate' | 'reasonDtm' | 'nextJudgeDtm'>,
  extra: SignalTextContext = {},
): SignalTextContext {
  return { reasonDate: item.reasonDate, reasonDtm: item.reasonDtm, nextJudgeDtm: item.nextJudgeDtm, ...extra };
}

export interface SignalPresentation {
  /** 상태명 (8자 안팎 명사형) */
  name: string;
  /** 한 줄 문구 — 상세창 요약·진행 단계 */
  line: string;
  /** 신호 팝오버 사유 줄 (③에 별도 표기가 없으면 line 과 같음) */
  reason: string;
  /** 처리 버튼 문구(③ 버튼 열). 없으면 null */
  button: string | null;
}

// ── 날짜·시각 서식 (940 ① 규칙) ────────────────────────────────────────────────

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})/;
const TIME_RE = /[T ](\d{2}):(\d{2})/;

function ymd(v: string | Date | null | undefined): { y: number; m: number; d: number } | null {
  if (v == null || v === '') return null;
  if (v instanceof Date) {
    return Number.isNaN(v.getTime()) ? null : { y: v.getFullYear(), m: v.getMonth() + 1, d: v.getDate() };
  }
  const m = DATE_RE.exec(v);
  return m ? { y: +m[1], m: +m[2], d: +m[3] } : null;
}

/** M/D — 연도 없음 */
export function formatMD(v: string | Date | null | undefined): string | null {
  const p = ymd(v);
  return p ? `${p.m}/${p.d}` : null;
}

/** HH:MM (24시간) */
export function formatHM(v: string | null | undefined): string | null {
  if (!v) return null;
  const m = TIME_RE.exec(v);
  return m ? `${m[1]}:${m[2]}` : null;
}

/** M/D HH:MM — 시각이 없으면 M/D 만 */
export function formatMDHM(v: string | null | undefined): string | null {
  const md = formatMD(v);
  if (!md) return null;
  const hm = formatHM(v);
  return hm ? `${md} ${hm}` : md;
}

function dayNumber(v: string | Date | null | undefined): number | null {
  const p = ymd(v);
  return p ? Math.floor(Date.UTC(p.y, p.m - 1, p.d) / 86400000) : null;
}

/** to 가 from 보다 며칠 뒤인지(to 가 앞이면 음수). 한쪽이 없으면 null */
export function daysBetween(from: string | Date | null | undefined, to: string | Date | null | undefined): number | null {
  const a = dayNumber(from);
  const b = dayNumber(to);
  return a == null || b == null ? null : b - a;
}

/** 하루 전 달력일 'YYYY-MM-DD' */
function dayBefore(v: string | null | undefined): string | null {
  const n = dayNumber(v);
  if (n == null) return null;
  const dt = new Date((n - 1) * 86400000);
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${dt.getUTCFullYear()}-${mm}-${dd}`;
}

/** 구절 조립 — 값이 없는 구절은 건너뛰고 ' · ' 로 잇는다 */
function join(parts: (string | null | false | undefined)[]): string {
  return parts.filter((p): p is string => !!p).join(' · ');
}

/** 기준일 대비 N일 지남(양수). 아직 안 지났거나 값이 없으면 null */
function overdueDays(base: string | Date | null | undefined, ctx: SignalTextContext): number | null {
  const d = daysBetween(base, ctx.today ?? new Date());
  return d != null && d > 0 ? d : null;
}

/** 'N일 지남' 구절. 당일이거나 값이 없으면 null */
function overdueText(base: string | Date | null | undefined, ctx: SignalTextContext): string | null {
  const n = overdueDays(base, ctx);
  return n ? `${n}일 지남` : null;
}

/** 착공까지 남은 일수 (착공일·기준일이 없으면 null, 이미 지났으면 음수) */
function startLeft(ctx: SignalTextContext): number | null {
  return daysBetween(ctx.today ?? new Date(), ctx.startDate);
}

/** 착공 상대 표현: 오늘/내일/모레/D-N. 값이 없거나 이미 지났으면 null */
function startRelative(ctx: SignalTextContext): string | null {
  const left = startLeft(ctx);
  if (left == null || left < 0) return null;
  if (left === 0) return '오늘';
  if (left === 1) return '내일';
  if (left === 2) return '모레';
  return `D-${left}`;
}

const dateOf = (ctx: SignalTextContext): string | null => ctx.reasonDate ?? ctx.reasonDtm ?? null;

const rankText = (ctx: SignalTextContext): string => (ctx.rank != null && ctx.rank >= 1 ? `${ctx.rank}순위까지` : '정한 순위까지');

// ── 신호 사유별 문구 표 ────────────────────────────────────────────────────────

interface ReasonDef {
  slot: SignalSlot;
  name: string;
  button: string | null;
  build: (ctx: SignalTextContext) => { line: string; reason?: string };
}

const fixed = (line: string, reason?: string): ReasonDef['build'] => () => ({ line, reason });

const REASONS: Record<SignalReasonCode, ReasonDef> = {
  // ── 반장 (940 행 29~37 · 상태명 '배정임박' 은 940 시트1 F114) ──
  FOREMAN_UNASSIGNED: { slot: 'FOREMAN', name: '미배정', button: null, build: fixed('배정 전이에요') },
  // 노랑 4종(자동배정·직접배정·다시 보내기 필요·배송정보 대기)은 처리 버튼을 두지 않는다(941 S15-2 — 버튼은 빨강 줄과 노랑 3종 14B-1·14C-1·14C-2 에만)
  FOREMAN_ASSIGNED_AUTO: {
    slot: 'FOREMAN', name: '자동배정', button: null, build: fixed('아직 일정확인 카톡을 보내지 않았어요'),
  },
  FOREMAN_ASSIGNED_MANUAL: {
    slot: 'FOREMAN', name: '직접배정', button: null, build: fixed('아직 일정확인 카톡을 보내지 않았어요'),
  },
  FOREMAN_UNASSIGNABLE: { slot: 'FOREMAN', name: '배정불가', button: '반장 배정', build: fixed('맡길 반장님이 없어요') },
  FOREMAN_UNASSIGNED_SOON: {
    slot: 'FOREMAN', name: '배정임박', button: '반장 배정',
    // 940 시트4 ③ 상황별 표현표에 한 줄 문구 틀이 없어 상태 정의(착공 D-2 이내 '가능' 회신 전, 930 V2.0 S15-1)로 조립
    build: ctx => {
      const rel = startRelative(ctx);
      const label = rel === '오늘' || rel === '내일' || rel === '모레' ? `${rel} 착공인데` : '착공이 임박했는데';
      return { line: `${label} 반장님 일정이 아직 확정되지 않았어요` };
    },
  },
  FOREMAN_WAITING: {
    slot: 'FOREMAN', name: '회신 대기', button: null,
    build: ctx => {
      const sent = formatMDHM(ctx.reasonDtm);
      const due = formatMDHM(ctx.nextJudgeDtm);
      return { line: join([sent && `${sent} 발송`, due && `기한 ${due}`]), reason: due ? `기한 ${due}` : '' };
    },
  },
  FOREMAN_REWAITING: {
    slot: 'FOREMAN', name: '재회신 대기', button: null,
    build: ctx => {
      const sent = formatMD(ctx.reasonDtm);
      const due = formatMDHM(ctx.nextJudgeDtm);
      return { line: join([sent && `일정 변경 카톡 ${sent} 발송`, due && `기한 ${due}`]) };
    },
  },
  FOREMAN_NEXT_WAITING: {
    slot: 'FOREMAN', name: '차순위 회신 대기', button: null,
    build: ctx => {
      const due = formatMDHM(ctx.nextJudgeDtm);
      const r = ctx.rank != null && ctx.rank >= 2 ? ctx.rank : null;
      const head = r ? `${r - 1}순위 '불가' → ${r}순위 회신 대기` : "이전 순위 '불가' → 다음 순위 회신 대기";
      return { line: join([head, due && `기한 ${due}`]) };
    },
  },
  FOREMAN_CONFIRMED: {
    slot: 'FOREMAN', name: '일정가능', button: null,
    build: ctx => {
      const md = formatMD(dateOf(ctx));
      const s = formatMD(ctx.startDate);
      const e = formatMD(ctx.endDate);
      const who = [ctx.partnerName, md && `${md} '가능'`].filter(Boolean).join(' ');
      return { line: join([md && `${md} '가능' 회신`, s && e && `${s} ~ ${e} 작업 확정`]), reason: who };
    },
  },
  FOREMAN_DECLINED: {
    slot: 'FOREMAN', name: '불가 회신', button: '다음 반장님께 보내기',
    build: ctx => {
      const md = formatMD(dateOf(ctx));
      return { line: join([md && `${md} '불가' 회신`, '다음 반장님 발송 전']) };
    },
  },
  FOREMAN_UNFIXED: {
    slot: 'FOREMAN', name: '미확정', button: '다음 반장님께 보내기',
    build: ctx => ({ line: `${rankText(ctx)} 답이 없어요 · 할일확인` }),
  },

  // SIF-1 자재시공 공종 — 시공 신호는 색 NONE(빗금). 서버 SignalText 문구와 같다
  FOREMAN_NA: { slot: 'FOREMAN', name: '해당 없음', button: null, build: fixed('자재파트너가 시공해요') },

  // ── 작업 (940 행 38~45) ──
  WORK_NOT_REQUESTED: {
    slot: 'WORK', name: '작업요청전', button: null,
    build: ctx => {
      const eve = formatMD(dayBefore(ctx.startDate));
      const s = formatMD(ctx.startDate);
      return { line: eve ? `착공 전날(${eve})까지 보내요` : '착공 전날까지 보내요', reason: s ? `착공 ${s}` : '' };
    },
  },
  WORK_REQUEST_DUE: {
    slot: 'WORK', name: '작업요청 대기', button: '작업요청 보내기',
    // 940 은 D-1('내일 착공') 기준이나 확정 결정 3 으로 D-2 부터 노랑 — 상대 표현을 값으로 조립
    build: ctx => {
      const rel = startRelative(ctx);
      const s = formatMD(ctx.startDate);
      const head = rel === '오늘' || rel === '내일' || rel === '모레' ? `${rel} ${s ?? ''}`.trim() : s;
      return { line: join([head && `${head} 착공`, '오늘 작업요청을 보내 주세요']) };
    },
  },
  WORK_REPORT_WAIT: {
    slot: 'WORK', name: '보고 대기', button: null,
    build: ctx => {
      const sent = formatMDHM(ctx.reasonDtm);
      const s = formatMD(ctx.startDate);
      const e = formatMD(ctx.endDate);
      return {
        line: join([sent && `${sent} 발송`, ctx.orderCount != null && `작업지시 ${ctx.orderCount}건`]),
        reason: s && e ? `${s} ~ ${e} 작업` : '',
      };
    },
  },
  WORK_REPORT_PROGRESS: {
    slot: 'WORK', name: '진행중 보고', button: null,
    build: ctx => {
      const span = daysBetween(ctx.startDate, ctx.endDate);
      const total = ctx.totalDays ?? (span != null ? span + 1 : null);
      const md = formatMD(dateOf(ctx));
      return {
        line: ctx.reportDays != null && total != null ? `작업 중 · 보고 ${ctx.reportDays}/${total}일` : '작업 중',
        reason: md ? `${md} 진행중 보고` : '',
      };
    },
  },
  WORK_REPORT_DELAY: {
    slot: 'WORK', name: '보고 지연', button: '리마인드',
    build: ctx => {
      const md = formatMD(ctx.endDate ?? ctx.reasonDate);
      return { line: md ? `작업일(${md}) 다음 날 오전 11시까지 보고 없음` : '작업일 다음 날 오전 11시까지 보고 없음' };
    },
  },
  WORK_REPORT_ISSUE: {
    slot: 'WORK', name: '이슈보고', button: '조치하기 ›',
    build: ctx => ({ line: ctx.issueType ? `이슈 포함 보고 · '${ctx.issueType}'` : '이슈 포함 보고' }),
  },
  WORK_INSPECT_WAIT: {
    slot: 'WORK', name: '검수 대기', button: '검수하기 ›',
    build: ctx => {
      const md = formatMD(dateOf(ctx));
      return { line: join([md && `완료 보고 ${md}`, '사장님 검수 대기']) };
    },
  },
  WORK_DONE: {
    slot: 'WORK', name: '작업 완료', button: null,
    build: ctx => {
      const md = formatMD(dateOf(ctx));
      return { line: join([md && `완료 보고 ${md}`, '사장님 검수완료']), reason: md ? `${md} 검수완료` : '' };
    },
  },

  // ── 발주 (940 행 47~60) ──
  ORDER_NA: { slot: 'ORDER', name: '해당 없음', button: null, build: fixed('자재 발주가 필요 없는 공종이에요') },
  ORDER_BEFORE_DATE: {
    slot: 'ORDER', name: '발주일 도래전', button: null,
    build: ctx => {
      const md = formatMD(ctx.reasonDate);
      return { line: join(['아직 보내지 않았어요', md && `발주일 ${md}`]), reason: md ? `발주일 ${md}에 보내요` : '' };
    },
  },
  ORDER_SEND_DUE: {
    slot: 'ORDER', name: '발주정보요청대기', button: '발주정보 보내기',
    build: fixed('오늘이 발주일이에요 · 발주정보를 보내 주세요', '오늘이 발주일'),
  },
  ORDER_SEND_OVERDUE: {
    slot: 'ORDER', name: '미발주', button: '발주정보 보내기',
    build: ctx => {
      const md = formatMD(ctx.reasonDate);
      const over = overdueText(ctx.reasonDate, ctx);
      return {
        line: md ? `발주일(${md})이 지났어요 · 발주정보 미발송` : '발주일이 지났어요 · 발주정보 미발송',
        reason: md ? `발주일 ${md} 경과${over ? ` (${over})` : ''}` : '',
      };
    },
  },
  ORDER_REPLY_WAIT: {
    slot: 'ORDER', name: '회신 대기', button: null,
    build: ctx => {
      const sent = formatMDHM(ctx.reasonDtm);
      return { line: join([sent && `${sent} 발송`, '거래처 회신을 기다리는 중']) };
    },
  },
  ORDER_CONFIRMED: {
    slot: 'ORDER', name: '발주 확정', button: null,
    build: ctx => {
      const md = formatMD(ctx.receiptDate ?? ctx.reasonDate);
      return { line: join(["'가능' 회신", md && `입고 ${md} 확정`]) };
    },
  },
  ORDER_REASSIGN_NEEDED: {
    slot: 'ORDER', name: '재배정 필요', button: '다른 거래처 고르기', build: fixed("'불가' 회신 · 대체 거래처를 골라 주세요"),
  },
  ORDER_REREPLY_WAIT: { slot: 'ORDER', name: '재회신 대기', button: null, build: fixed('바뀐 입고일로 다시 보냄 · 회신을 기다리는 중') },
  ORDER_RESEND_NEEDED: { slot: 'ORDER', name: '다시 보내기 필요', button: null, build: fixed('입고일이 바뀌었어요 · 거래처에 다시 보내 주세요') },
  ORDER_SHIP_INFO_WAIT: {
    slot: 'ORDER', name: '배송정보 대기', button: null,
    build: ctx => {
      const md = formatMD(ctx.receiptDate ?? ctx.reasonDate);
      return { line: "'가능' 회신 · 출고 · 배송정보를 기다리는 중", reason: md ? `입고 ${md}` : '' };
    },
  },
  ORDER_TODAY_DELIVERY: {
    slot: 'ORDER', name: '오늘의 배송', button: null,
    build: ctx => {
      const arrive = formatMDHM(ctx.reasonDtm);
      return {
        line: join([
          arrive && `도착 ${arrive}`,
          [ctx.driverName, ctx.driverPhone].filter(Boolean).join(' ') || null,
          ctx.photoCount != null && `상차사진 ${ctx.photoCount}장`,
        ]),
      };
    },
  },
  ORDER_RECEIPT_WAIT: {
    slot: 'ORDER', name: '입고등록대기', button: '배송정보현황 보기',
    build: ctx => {
      const md = formatMD(ctx.receiptDate ?? ctx.reasonDate);
      return { line: '오늘 입고 예정 · 입고 확인 전', reason: md ? `오늘 ${md} 입고` : '' };
    },
  },
  ORDER_RECEIVED: {
    slot: 'ORDER', name: '입고 완료', button: null,
    build: ctx => ({
      line: join(['입고 완료', formatMD(ctx.receiptDate ?? ctx.reasonDate), ctx.photoCount != null && `반장님 하차사진 ${ctx.photoCount}장`]),
    }),
  },
  // 입고는 끝났으나 부족·파손 이슈가 조치 전 — 빨강. 합류된 작업보고(14C-3)에서 조치하면 녹색으로 돌아온다
  ORDER_RECEIPT_ISSUE: {
    slot: 'ORDER', name: '입고 이슈', button: '조치하기 ›',
    build: ctx => {
      const md = formatMD(ctx.receiptDate ?? ctx.reasonDate);
      return { line: md ? `입고 완료(${md}) · 부족·파손 이슈 조치 전` : '입고 완료 · 부족·파손 이슈 조치 전', reason: '입고 이슈 조치 전' };
    },
  },
  ORDER_RECEIPT_DELAY: {
    slot: 'ORDER', name: '입고 지연', button: '배송정보현황 보기',
    build: ctx => {
      const md = formatMD(ctx.receiptDate ?? ctx.reasonDate);
      return { line: md ? `입고일(${md})이 지났어요 · 입고 미확인` : '입고일이 지났어요 · 입고 미확인', reason: md ? `입고일 ${md} 경과` : '' };
    },
  },
};

/**
 * 신호 사유 코드 → 상태명·한 줄 문구·팝오버 사유·버튼. 모르는 코드는 null(호출부가 오류·빈 표시로 처리, 임의 문구를 만들지 않음).
 * 한 줄 문구는 940 ③ 'F 한 줄 문구 틀'을, 팝오버 사유는 같은 열의 '/ 팝오버 사유' 표기가 있으면 그것을 쓴다.
 */
export function signalPresentation(reasonCode: string, ctx: SignalTextContext = {}): SignalPresentation | null {
  const def = (REASONS as Record<string, ReasonDef | undefined>)[reasonCode];
  if (!def) return null;
  const { line, reason } = def.build(ctx);
  return { name: def.name, line, reason: reason || line, button: def.button };
}

/** 사유 코드가 속한 신호 슬롯 (모르는 코드는 null) */
export function reasonSlot(reasonCode: string): SignalSlot | null {
  return (REASONS as Record<string, ReasonDef | undefined>)[reasonCode]?.slot ?? null;
}

export const SIGNAL_SLOT_LABEL: Record<SignalSlot, string> = { FOREMAN: '시공파트너', ORDER: '발주', WORK: '작업' };
